import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { SingleValue } from 'react-select';
import { AdvancedSelect, Decorator, Input } from "@myhealth-raccoon/components";
import { validation } from "@myhealth-raccoon/forms";
import { useAsyncData } from "@myhealth-raccoon/services";
import { MarkerPin01 } from "@untitled-ui/icons-react";
import classNames from "classnames";

import { addressService, Place } from "src/services/address";
import { useDebounce } from "src/hooks/useDebounce";
import { PatientDetailsUpdateRequest } from "src/services/patient";
import { SuburbOptionType, SuburbSelect } from "./suburbs";
import { useRememberFormValues } from "src/hooks/useRememberFormValues";

type Props = {
    shouldShowValidation?: boolean;
    className?: string;
    prepend?: React.ReactNode;
    append?: React.ReactNode;
    editGender: boolean;
    editAddress: boolean;
}

type Form = Pick<PatientDetailsUpdateRequest, "address" | "gender">

export interface BasicInfoFormRef {
    submit(): Promise<PatientDetailsUpdateRequest>;
}

export const BasicInfoForm = forwardRef<BasicInfoFormRef, Props>(({ shouldShowValidation, className, prepend, append, editAddress, editGender }, ref) => {
    const [defaultValues, rememberDefaultValues] = useRememberFormValues<Form>('basicInfoForm', "session");

    const [addressMode, setAddressMode] = useState<"search" | "manual">("search");
    const {
        register, setValue, watch, setError, formState: { errors }, handleSubmit, clearErrors
    } = useForm<Form>({
        defaultValues: defaultValues
    });

    const [placesSearch, setPlacesSearch] = useState("");
    const { data: places, isLoading: isSearching } = useAsyncData({
        service: addressService,
        method: addressService.GetSearchPlace,
        invokeOnMount: !!placesSearch
    }, placesSearch);

    const [selectedPlace, setSelectedPlace] = useState<Place>();

    useEffect(() => {
        const findPlace = async (placeId: string) => {
            const { description, city, postcode } = await addressService.GetFullAddress(placeId);
            const regx = new RegExp(`, ${city}.*`); // trim full address following ", city..."

            const placeAddressLine = description.replace(regx, '');
            setValue('address.address', placeAddressLine);
            setValue('address.city', city);
            setValue('address.postcode', postcode);
            clearErrors("address");
        }
        if (selectedPlace) {
            findPlace(selectedPlace.placeId);
        }
    }, [clearErrors, selectedPlace, setValue]);

    const inputSearchPlaces = useDebounce(useCallback((newValue: string) => {
        setPlacesSearch(newValue);
    }, []), 300)

    const selectSuburb = useCallback((option?: SuburbOptionType | SingleValue<SuburbOptionType>) => {
        if (option) {
            setValue("address.city", option.suburb)
            setValue("address.postcode", option.postcode);
            clearErrors("address.city");
            clearErrors("address.postcode");
        } else {
            setValue("address.city", undefined!)
            setValue("address.postcode", undefined!);
        }
        setSelectedPlace(undefined);
    }, [clearErrors, setValue]);

    useImperativeHandle(ref, () => ({
        submit: async () => {
            return new Promise((resolve, reject) => {
                handleSubmit(async (form) => {
                    rememberDefaultValues(form);
                    const parsedForm = { ...form };
                    if (!editAddress) {
                        delete parsedForm.address;
                    } else {
                        if (!form.address?.city) {
                            setError("address.city", { message: "This field is required.", type: "required" })
                        }
                        if (!form.address?.postcode) {
                            setError("address.postcode", { message: "This field is required.", type: "required" })
                        }
                        if (!form.address?.address || !form.address?.city || !form.address?.postcode) {
                            throw "Validation error";
                        }
                    }
                    if (!editGender) {
                        delete parsedForm.gender
                    }
                    resolve(parsedForm);
                }, reject
                )()
            })
        }
    }));

    const genderRadio = register("gender", { validate: editGender ? validation.required() : undefined });

    const address = register("address.address", { validate: editAddress ? validation.required() : undefined });
    register("address.city", { validate: editAddress ? validation.required() : undefined });
    register("address.postcode", { validate: editAddress ? validation.required() : undefined });

    return <>
        {prepend}
        <form className={classNames("space-y-4", className)}>
            {editGender && <Decorator fieldset requiredIndicator
                label="Gender"
                fieldError={shouldShowValidation ? errors.gender : undefined}
            >
                <div className="flex flex-row gap-5 text-base py-1.5">
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            {...genderRadio}
                            value="Male"
                            type="radio"
                            className="radio align-bottom text-xl mt-0.5"
                            id="genderMale"
                        />
                        <label htmlFor="GenderMale" className="text-gray-700 cursor-pointer">Male</label>
                    </Decorator>
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            {...genderRadio}
                            value="Female"
                            type="radio"
                            className="radio text-xl mt-0.5"
                            id="genderFemale"
                        />
                        <label htmlFor="genderFemale" className="text-gray-700 cursor-pointer">Female</label>
                    </Decorator>
                    <Decorator
                        className="flex flex-row gap-3">
                        <input
                            {...genderRadio}
                            value="Non-binary"
                            type="radio"
                            className="radio text-xl mt-0.5"
                            id="genderOther"
                        />
                        <label htmlFor="genderOther" className="text-gray-700 cursor-pointer">Other</label>
                    </Decorator>
                </div>
            </Decorator>}
            {editAddress && <div>
                {addressMode === "search" && <>
                    <Decorator label="Address" requiredIndicator fieldError={shouldShowValidation ? errors.address?.root || errors.address?.address : undefined}>
                        <AdvancedSelect<Place, false>
                            error={shouldShowValidation && !!(errors.address?.root || errors.address?.address)}
                            options={places}
                            onInputChange={inputSearchPlaces}
                            getOptionLabel={place => place.description}
                            isLoading={isSearching}
                            isOptionSelected={place => selectedPlace?.placeId === place.placeId}
                            onChange={(place) => setSelectedPlace(place as Place)}
                            value={selectedPlace}
                            filterOption={_ => true}
                            Icon={MarkerPin01}
                            placeholder="Start typing your address"
                            defaultInputValue={[watch('address.address'), watch('address.city'), watch('address.postcode')].filter(x => x).join(', ') || undefined}
                        />
                    </Decorator>
                    <button type="button" className="text-primary font-semibold mt-1.5" onClick={() => setAddressMode("manual")}>Enter manually instead</button>
                </>}
                {addressMode === "manual" && <>
                    <div className="space-y-2">
                        <Input
                            label="Street Address"
                            requiredIndicator
                            placeholder="Please enter unit/street number & street "
                            id="address1"
                            {...address}
                            fieldError={shouldShowValidation ? errors.address?.address : undefined}
                        />
                        <Decorator label="Suburb"
                            requiredIndicator fieldError={shouldShowValidation ? (errors.address?.city || errors.address?.postcode) : undefined}>
                            <SuburbSelect
                                error={shouldShowValidation && !!(errors.address?.city || errors.address?.postcode)}
                                onChange={selectSuburb}
                                postcode={watch("address.postcode")}
                                suburb={watch("address.city")}
                            />
                        </Decorator>
                    </div>
                    <button type="button" className="text-primary font-semibold mt-1.5" onClick={() => setAddressMode("search")}>Use address lookup</button>
                </>}
            </div>}
        </form >
        {append}
    </>;
})